import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString } from "geojson";

import { DRAWING_CONSTANTS } from "fond/constants";

import directSelect from "../../base/directSelect";

import { isValid } from "./helper";

const {
  constants: Constants,
  lib: { createSupplementaryPoints },
} = MapboxDraw;

interface DrawCustomModeState {
  selectedCoordPaths: string[];
  featureId: string | undefined;
}

interface DrawCustomModeExtension<S> extends DrawCustomMode<S> {
  clickInactive(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
  fireActionable(this: DrawCustomModeThis, state: S): void;
  dragMove(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
}

const directSelectLeadIns: DrawCustomModeExtension<DrawCustomModeState> = {
  ...(directSelect as DrawCustomModeExtension<DrawCustomModeState>),

  /**
   * We override the toDisplayFeatures method to:
   * - disable the creation of midpoints
   * - set the valid state based on if the feature is connected.
   */
  toDisplayFeatures: function (state, geojson: GeoJSON.Feature<GeoJSON.LineString>, display) {
    if (state.featureId === geojson.properties?.id) {
      if (geojson.properties) {
        geojson.properties.active = Constants.activeStates.ACTIVE;
        geojson.properties[`user_${DRAWING_CONSTANTS.Valid}`] = String(isValid(geojson, this.map, this._ctx.options.modeOptions));
      }
      display(geojson);

      createSupplementaryPoints(geojson, {
        midpoints: false,
        selectedPaths: state.selectedCoordPaths,
      }).forEach(display);
    } else {
      if (geojson.properties) {
        geojson.properties.active = Constants.activeStates.INACTIVE;
      }
      display(geojson);
    }
    this.fireActionable(state);
  },

  onMouseUp(state, event) {
    const parentFeature = state.featureId ? this.getFeature(state.featureId) : undefined;
    if (parentFeature && parentFeature.id) {
      this._ctx.api.setFeatureProperty(
        parentFeature.id,
        DRAWING_CONSTANTS.Valid,
        isValid(parentFeature.toGeoJSON() as Feature<LineString>, this.map, this._ctx.options.modeOptions).toString()
      );
    }

    directSelect.onMouseUp?.call(this, state, event);
  },

  /**
   * If the user clicks on another feature (currently inactive) we keep the user in
   * the direct select mode and select the new feature.
   */
  clickInactive: function (_, event) {
    const featureId = event.featureTarget.properties?.id;
    this.changeMode(Constants.modes.DIRECT_SELECT, { featureId });
  },
};

export default directSelectLeadIns;
